import React, { useState } from 'react';
import {
  Card, CardContent, Box, Typography, Chip, Button,
  Collapse, IconButton, Divider, Stack
} from '@mui/material';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import AddModeratorIcon from '@mui/icons-material/AddModerator';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import CoverageChips from './CoverageChips';
import DeductiblesTable from './DeductiblesTable';
import { packageCatalog, getAllCoverages } from './data/packageCatalog';
import { getDeductiblesForPackage } from './data/deductiblesRules';

const packageIcons = {
  Base: SecurityIcon,
  Medium: VerifiedUserIcon,
  Collisione: AddModeratorIcon,
  Kasko: GppMaybeIcon
};

// Map to MUI color names or custom hex
const colorMap = {
  Base: { main: '#1976d2', light: '#e3f2fd', contrast: '#fff' },
  Medium: { main: '#ed6c02', light: '#fff3e0', contrast: '#fff' },
  Collisione: { main: '#2e7d32', light: '#e8f5e9', contrast: '#fff' },
  Kasko: { main: '#7b1fa2', light: '#f3e5f5', contrast: '#fff' }
};

const formatPrice = (price) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(price);

export default function PackageCard({ quote, packageType, onAction }) {
  const [deductiblesOpen, setDeductiblesOpen] = useState(false);

  const pkg = packageCatalog[packageType];
  const Icon = packageIcons[packageType];
  const color = colorMap[packageType];
  const coverages = getAllCoverages(packageType);
  const deductibles = getDeductiblesForPackage(packageType, quote.zone);

  return (
    <Card elevation={3} sx={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Colored Header */}
      <Box sx={{ bgcolor: color.main, p: 2.5, color: color.contrast }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <Icon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} color="inherit">{pkg.name}</Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }} color="inherit">{pkg.description}</Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Chip label={`Zona ${quote.zone}`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }} variant="outlined" />
          <Chip label={`${quote.duration} mesi`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', borderColor: 'rgba(255,255,255,0.4)' }} variant="outlined" />
        </Stack>
      </Box>

      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0 }}>
        {/* Price */}
        <Box sx={{ textAlign: 'center', py: 3, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">Premio totale</Typography>
          <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ lineHeight: 1.2 }}>
            {formatPrice(quote.prices[packageType])}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {formatPrice(quote.prices[packageType] / parseInt(quote.duration))} / mese
          </Typography>
        </Box>

        {/* Coverages */}
        <Box sx={{ py: 2.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary"
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            <CheckIcon fontSize="inherit" sx={{ color: 'success.main' }} />
            Coperture incluse
          </Typography>
          <CoverageChips coverages={coverages} color={packageType} />
        </Box>

        {/* Deductibles Accordion */}
        <Box>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5, cursor: 'pointer' }}
            onClick={() => setDeductiblesOpen(p => !p)}
          >
            <Typography variant="body2" fontWeight={700}>Scoperti e Franchigie</Typography>
            <IconButton size="small">
              {deductiblesOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Collapse in={deductiblesOpen}>
            <DeductiblesTable deductibles={deductibles} />
          </Collapse>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, pt: 2, borderTop: 1, borderColor: 'divider', mt: 'auto' }}>
          <Button variant="outlined" size="small" sx={{ flex: 1 }} startIcon={<SaveIcon />}
            onClick={() => onAction?.('draft', packageType)}>
            Bozza
          </Button>
          <Button variant="outlined" size="small" sx={{ flex: 1 }} startIcon={<SendIcon />}
            onClick={() => onAction?.('send', packageType)}>
            Invia
          </Button>
          <Button
            variant="contained" size="small" sx={{ flex: 1, bgcolor: color.main, '&:hover': { bgcolor: color.main, filter: 'brightness(0.9)' } }}
            startIcon={<CheckCircleIcon />}
            onClick={() => onAction?.('issue', packageType)}
          >
            Emetti
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}